'use client';

import { Alert, AlertDescription } from '@ophelos/ui/components/alert';
import { Button } from '@ophelos/ui/components/button';
import { AttachFileButton, FileAttachmentList } from '@ophelos/ui/components/file-attachments';
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupTextarea } from '@ophelos/ui/components/input-group';
import { ScrollArea } from '@ophelos/ui/components/scroll-area';
import { cn } from '@ophelos/ui/lib/utils';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightFromLine, ArrowUp, LockKeyhole, MessagesSquare, X } from 'lucide-react';
import { MarkdownContent } from '@ophelos/ui/components/markdown-content';
import { type CSSProperties, type RefObject, useEffect, useRef, useState } from 'react';
import { type ChatMessage, type HandoffRequest, useAIPanel } from '@/contexts/AIContext';
import { useConfiguration } from '@/contexts/ConfigurationContext';
import { useAuth } from '@/contexts/customer-auth-context';
import { useLanguage } from '@/contexts/LanguageContext';
import { useChatAttachments } from '@/hooks/useChatAttachments';
import { MOBILE_QUERY, useMediaQuery } from '@/hooks/useMediaQuery';
import { useTranslation } from '@/hooks/useTranslation';
import { isEscalationCompletedEvent, isEscalationFailedEvent } from '@/lib/escalation';
import { ACCOUNT_DIGEST_KEY, accountDigest, offerablePages, type PortalLinkState, type PortalPageRequest, portalPageRequestFromEvent } from '@/lib/portalNavigation';
import { PortalPageButtons } from '@/components/ui/PortalPageButtons';
import { useDebtData } from '@/contexts/DebtDataContext';
import { useBudgetAssessment } from '@/hooks/useBudgetAssessment';
import { Empty, EmptyHeader, EmptyMedia, EmptyTitle } from '@ophelos/ui/components/empty';

export interface AIPanelContentProps {
  context?: 'login' | 'dashboard' | 'payment' | 'account';
}

const AGENTOS_BASE_URL = import.meta.env.VITE_AGENTOS_URL || 'https://agents.localhost';

// Contextual suggestions will be generated dynamically using translations
function getContextualSuggestions(context: 'login' | 'dashboard' | 'payment' | 'account', t: (key: string) => string): string[] {
  switch (context) {
    case 'login':
      return [
        t('aiHelper.suggestions.login.findReferenceCode'),
        t('aiHelper.suggestions.login.lostCode'),
        t('aiHelper.suggestions.login.informationSecure'),
      ];
    case 'dashboard':
      return [
        t('aiHelper.suggestions.dashboard.howCanIPay'),
        t('aiHelper.suggestions.dashboard.alreadyPaid'),
        t('aiHelper.suggestions.dashboard.whatIsThisDebt'),
      ];
    case 'payment':
      return [
        t('aiHelper.suggestions.payment.bestPaymentOption'),
        t('aiHelper.suggestions.payment.changePaymentDate'),
        t('aiHelper.suggestions.payment.missPayment'),
      ];
    case 'account':
      return [
        t('aiHelper.suggestions.account.viewPaymentHistory'),
        t('aiHelper.suggestions.account.downloadStatements'),
        t('aiHelper.suggestions.account.updateContactInfo'),
      ];
    default:
      return [];
  }
}

// The agent offers a handoff in one reply and makes it when the customer agrees, so what they
// asked for is the message before their yes, unless the handoff came on their first message.
function handoffRequest(earlier: ChatMessage[], sent: ChatMessage): HandoffRequest {
  const asked = earlier.filter((m) => m.role === 'user').pop() ?? sent;
  return { message: asked.content, sentAt: asked.timestamp };
}

// Panel content component (used in global fixed panel and mobile drawer)
export function AIPanelContent({ context = 'dashboard' }: AIPanelContentProps) {
  const { t } = useTranslation();
  const { closePanel, conversation, appendMessage, markHandedOver, markHandoffFailed } = useAIPanel();
  const { accountId, sessionId, messages, handoff, failedHandoffRequest } = conversation;
  const { authStage, getAccessToken } = useAuth();
  const { currentLanguage } = useLanguage();
  const { config } = useConfiguration();
  const { debts, accountPaymentPlan, isLoading: debtsLoading } = useDebtData();
  const budget = useBudgetAssessment();
  // What this portal can open: sent to the agent with each run and resolved into its buttons.
  // The budget page sends the customer back on `not_required`, so only another state, read
  // without error, is open; while that read loads, only the budget page is held back.
  const linkState: PortalLinkState | null = debtsLoading
    ? null
    : { debts, accountPaymentPlan, budgetAssessmentOpen: !budget.isLoading && !budget.hasError && budget.state !== 'not_required', payment: config.payment };
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  // isStreaming can't stop a second send that lands before the re-render: the exiting
  // suggestions keep the previous render's handler. Two runs at once in one AgentOS session
  // each try to open its conversation.
  const sendingRef = useRef(false);
  // Scopes drag-and-drop uploads to the composer itself — window-level drag
  // detection means a drop anywhere in the portal would otherwise be handled
  // as a chat attachment (see useAttachmentUpload).
  const composerContainerRef = useRef<HTMLDivElement>(null);
  const attachments = useChatAttachments(accountId, authStage, getAccessToken, composerContainerRef);

  const suggestions = getContextualSuggestions(context || 'dashboard', t);

  // Scroll to bottom using the scroll container directly instead of scrollIntoView,
  // which on iOS mobile scrolls the entire page and displaces the drawer
  useEffect(() => {
    const viewport = scrollAreaRef.current;
    if (viewport) {
      requestAnimationFrame(() => {
        viewport.scrollTop = viewport.scrollHeight;
      });
    }
  }, [messages, isStreaming, streamingContent]);

  const isMobile = useMediaQuery(MOBILE_QUERY);

  // Auto-focus input on desktop only (on mobile, focusing triggers the keyboard which hides the input)
  useEffect(() => {
    if (messages.length === 0 && !isMobile) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [messages.length, isMobile]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const handleSend = async (directMessage?: string) => {
    const messageText = directMessage || input;
    // Until the debts load, the pages sent with the run are empty, and the agent tells the
    // customer that Pay all and every other page aren't available.
    if (!messageText.trim() || isStreaming || sendingRef.current || attachments.uploading || !linkState) return;

    if (!accountId) {
      appendMessage(sessionId, {
        id: Date.now().toString(),
        role: 'assistant',
        content: t('aiHelper.errors.pleaseLogIn'),
        timestamp: new Date(),
      });
      return;
    }

    const readyAttachments = attachments.items.filter((item) => item.status === 'ready');
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: messageText,
      timestamp: new Date(),
      attachments: readyAttachments.length > 0 ? readyAttachments : undefined,
    };

    appendMessage(sessionId, userMessage);
    const messageToSend = messageText;
    const sentAttachments = attachments.toChatAttachments();
    setInput('');
    attachments.clear();
    setIsStreaming(true);

    // Refocus textarea to keep mobile keyboard open and prevent drawer displacement
    inputRef.current?.focus();
    setStreamingContent('');

    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();
    sendingRef.current = true;

    try {
      // Build session state with auth credentials and UI language
      const digest = accountDigest(linkState);
      const sessionState: Record<string, any> = {
        ui_language: currentLanguage,
        ...offerablePages(linkState),
        ...(digest && { [ACCOUNT_DIGEST_KEY]: digest }),
      };

      if (authStage === 'full') {
        const workosJwt = await getAccessToken();
        if (workosJwt) {
          sessionState.auth_credential = workosJwt;
          sessionState.auth_type = 'jwt';
        }
      } else if (authStage === 'limited') {
        const accessCode = localStorage.getItem('access_code');
        if (accessCode) {
          sessionState.auth_credential = accessCode;
          sessionState.auth_type = 'access_code';
        }
      }

      if (sentAttachments.length > 0) {
        sessionState.attachments = sentAttachments;
      }

      // Use form data as AgentOS expects application/x-www-form-urlencoded
      const formData = new URLSearchParams();
      formData.append('message', messageToSend);
      formData.append('stream', 'true');
      // Pass account_id as dependency for escalation tool
      formData.append('dependencies', JSON.stringify({ account_id: accountId }));
      // Pass session_id to maintain conversation context
      if (sessionId) {
        formData.append('session_id', sessionId);
      }
      // Pass session_state with auth credentials for customer data loading
      if (Object.keys(sessionState).length > 0) {
        formData.append('session_state', JSON.stringify(sessionState));
      }

      // Never the system key: a VITE_ value is written into the page every visitor downloads.
      const headers: Record<string, string> = {
        'Content-Type': 'application/x-www-form-urlencoded',
      };

      // Add X-Access-Code header for Stage 1 authentication (so middleware can validate)
      if (authStage === 'limited') {
        const accessCode = localStorage.getItem('access_code');
        if (accessCode) {
          headers['X-Access-Code'] = accessCode;
        }
      }

      const response = await fetch(`${AGENTOS_BASE_URL}/agents/livechat-agent/runs`, {
        method: 'POST',
        headers,
        body: formData,
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body');
      }

      const decoder = new TextDecoder();
      let buffer = '';
      let currentEvent = '';
      let accumulatedContent = '';
      let handedOver = false;
      const offeredPages: PortalPageRequest[] = [];
      // Text the model writes after a tool call is a new message, streamed straight on
      // from the one before it with no separator.
      let resumesAfterTool = false;

      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');

        // Keep the last incomplete line in the buffer
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed) {
            // Empty line means end of event
            currentEvent = '';
            continue;
          }

          if (trimmed.startsWith('event: ')) {
            currentEvent = trimmed.substring(7);
          } else if (trimmed.startsWith('data: ')) {
            try {
              const jsonStr = trimmed.substring(6);
              if (jsonStr === '[DONE]') break;
              const event = JSON.parse(jsonStr);

              // Handle content streaming
              if (currentEvent === 'RunContent' && event.content) {
                const contentText = typeof event.content === 'string' ? event.content : '';
                if (resumesAfterTool && contentText && accumulatedContent && !/\s$/.test(accumulatedContent)) accumulatedContent += '\n\n';
                if (contentText) resumesAfterTool = false;
                accumulatedContent += contentText;
                setStreamingContent(accumulatedContent);
              }

              if (currentEvent === 'ToolCallCompleted') resumesAfterTool = true;

              // Keyed off the escalate_to_human tool-call event rather than the reply text, so it
              // holds across locales and phrasings. Recorded on the event itself: unmounting the
              // panel aborts the stream, so nothing after the loop is guaranteed to run. A retry after
              // a failed handoff, made or offered again, quotes what the failed one was for.
              if (isEscalationCompletedEvent(currentEvent, event)) {
                markHandedOver(sessionId, { messageId: userMessage.id, ...(failedHandoffRequest ?? handoffRequest(messages, userMessage)) });
                handedOver = true;
              } else if (isEscalationFailedEvent(currentEvent, event)) {
                markHandoffFailed(sessionId, handoffRequest(messages, userMessage));
              }

              const offered = portalPageRequestFromEvent(currentEvent, event);
              if (offered) offeredPages.push(offered);
            } catch (error) {
              console.error('Error parsing SSE event:', error, trimmed);
            }
          }
        }
      }

      // Add assistant message with accumulated content
      if (accumulatedContent) {
        appendMessage(sessionId, {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: accumulatedContent,
          timestamp: new Date(),
          ...(offeredPages.length > 0 && { pages: offeredPages }),
          ...(handedOver && { handoffReplyTo: userMessage.id }),
        });
      }
    } catch (error) {
      // Don't show error if it's an abort error (user cancelled)
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error('Error sending message:', error);
        // Show error message to user
        appendMessage(sessionId, {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: t('aiHelper.errors.connectionTrouble'),
          timestamp: new Date(),
        });
      }
    } finally {
      sendingRef.current = false;
      setIsStreaming(false);
      setStreamingContent('');
      abortControllerRef.current = null;
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    handleSend(suggestion);
  };

  // The agent's reply in the handoff turn becomes the handoff notice, in its own words. Without one
  // (lost when the panel closed mid-reply, or the stream failed) a fixed sentence stands in for it
  // after the customer's message, once nothing is streaming into that turn.
  const isHandoffReply = (m: ChatMessage) => handoff !== null && m.handoffReplyTo === handoff.messageId;
  const standsIn = (m: ChatMessage) =>
    handoff?.messageId === m.id && !messages.some(isHandoffReply) && !(isStreaming && m === messages[messages.length - 1]);
  const handoffNotice = (reply?: ChatMessage) =>
    handoff && (
      <Alert key="handoff" variant="info">
        <AlertDescription>
          {reply ? <MarkdownContent>{reply.content}</MarkdownContent> : <p>{t('aiHelper.handoff.passedOn')}</p>}
          {reply?.pages && <PortalPageButtons requests={reply.pages} linkState={linkState} />}
          {handoff.message && (
            <>
              <p className="mt-2 text-xs">
                {t('aiHelper.handoff.yourMessage')} ({handoff.sentAt.toLocaleDateString(currentLanguage, { day: '2-digit', month: '2-digit', year: '2-digit' })})
              </p>
              <p className="italic line-clamp-3 break-words">“{handoff.message}”</p>
            </>
          )}
        </AlertDescription>
      </Alert>
    );

  const messagesList = (
    <>
      <AnimatePresence>
        {messages.flatMap((message) => [
          isHandoffReply(message) ? (
            handoffNotice(message)
          ) : (
            <motion.div
              key={message.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className={cn('flex gap-3', message.role === 'user' ? 'justify-end' : 'justify-start')}
            >
              <div
                className={cn(
                  'text-sm leading-relaxed',
                  message.role === 'user'
                    ? 'max-w-[75%] rounded-3xl px-4 py-2 bg-accent border border-ring text-foreground ml-auto'
                    : 'text-foreground',
                )}
              >
                {message.role === 'assistant' ? <MarkdownContent>{message.content}</MarkdownContent> : message.content}
                {message.pages && <PortalPageButtons requests={message.pages} linkState={linkState} />}
                {message.attachments && message.attachments.length > 0 && <FileAttachmentList items={message.attachments} className="mt-1.5" />}
              </div>
            </motion.div>
          ),
          standsIn(message) && handoffNotice(),
        ])}
      </AnimatePresence>
      {isStreaming && streamingContent && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex gap-3">
          <div className="text-sm leading-relaxed text-foreground">
            <MarkdownContent>{streamingContent}</MarkdownContent>
          </div>
        </motion.div>
      )}
      {isStreaming && !streamingContent && (
        <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }} className="flex gap-3">
          <div className="flex gap-1">
            <motion.div
              className="w-2 h-2 rounded-full bg-muted-foreground/75"
              animate={{ y: [0, -5, 0] }}
              transition={{ duration: 0.6, repeat: Infinity }}
            />
            <motion.div
              className="w-2 h-2 rounded-full bg-muted-foreground/75"
              animate={{ y: [0, -5, 0] }}
              transition={{ duration: 0.6, repeat: Infinity, delay: 0.15 }}
            />
            <motion.div
              className="w-2 h-2 rounded-full bg-muted-foreground/75"
              animate={{ y: [0, -5, 0] }}
              transition={{ duration: 0.6, repeat: Infinity, delay: 0.3 }}
            />
          </div>
        </motion.div>
      )}
    </>
  );

  return (
    <div className="flex flex-col w-full h-full p-0 md:h-dvh md:py-2 md:pr-2 2xl:py-3 2xl:pr-3">
      <div
        ref={composerContainerRef}
        className="stack h-full bg-sidebar max-md:rounded-none md:rounded-xl border-l md:border border-border-heavy overflow-hidden"
      >
        <div className="flex items-center gap-2 p-2 max-md:border-b">
          <Button variant="outline" className="rounded-full md:hidden" size="icon" onClick={closePanel} aria-label={t('common.close')}>
            <X className="size-4" />
          </Button>

          <Button variant="outline" className="rounded-full ml-auto hidden md:flex" size="icon-sm" onClick={closePanel} aria-label={t('common.close')}>
            <ArrowRightFromLine className="size-3.5" />
          </Button>
        </div>
        {/* Messages area */}
        <div className="flex-1 overflow-hidden mb-4  min-h-0">
          <AnimatePresence mode="wait">
            {messages.length > 0 && (
              <motion.div
                key="messages"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="h-full"
              >
                {isMobile ? (
                  <div ref={scrollAreaRef} className="h-full px-6 overflow-y-auto flex flex-col justify-end">
                    <div className="space-y-4">
                      {messagesList}
                      <div className="h-px" />
                    </div>
                  </div>
                ) : (
                  <ScrollArea ref={scrollAreaRef} className="h-full px-6">
                    <div className="space-y-4">
                      {messagesList}
                      <div className="h-px" />
                    </div>
                  </ScrollArea>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Login prompt (shown instead of suggestions when not logged in) */}
        {!accountId && messages.length === 0 && (
          <div className="flex-1 flex items-center justify-center px-6">
            <Empty>
              <EmptyHeader>
                <EmptyMedia variant="icon" className="mt-6 mb-8">
                  <div className="p-4.25 border rounded-[2.75rem] bg-accent/25 border-ring/8">
                    <div className="p-3.75 border rounded-[1.75rem] shadow-lg bg-accent/60 border-ring/15 shadow-ring/10 dark:bg-accent/50">
                      <div className="p-2.75 border rounded-xl shadow-lg bg-card/80 border-ring/35 shadow-ring/15 dark:bg-card/25 dark:border-ring/50 dark:shadow-ring/15">
                        <LockKeyhole className="text-accent-foreground" />
                      </div>
                    </div>
                  </div>
                </EmptyMedia>
                <EmptyTitle>{t('aiHelper.placeholder.pleaseLogIn')}</EmptyTitle>
              </EmptyHeader>
            </Empty>
          </div>
        )}

        {/* Suggestions */}
        <AnimatePresence mode="wait">
          {accountId && messages.length === 0 && (
            <motion.div
              key="suggestions"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="mb-4 flex flex-wrap gap-2 px-4 pt-4"
            >
              {suggestions.map((suggestion, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2 + index * 0.1, duration: 0.2 }}
                  className="w-full"
                >
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!linkState}
                    onClick={() => handleSuggestionClick(suggestion)}
                    className="text-sm rounded-full h-auto py-2 px-4 shadow-none border-border-heavy wrap-normal text-left whitespace-normal!"
                  >
                    {suggestion}
                  </Button>
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Input area */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0, duration: 0.3 }} className="px-4 pb-4 ">
          {attachments.items.length > 0 && <FileAttachmentList items={attachments.items} onRemove={attachments.remove} className="mb-2" />}
          <InputGroup className={cn('min-h-[100px] bg-card rounded-xl', attachments.isDraggingOver && 'highlight-border-glow')}>
            {attachments.isDraggingOver && (
              <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-accent/80 backdrop-blur-sm pointer-events-none">
                <span className="text-sm font-medium text-accent-foreground">{t('aiHelper.dropFilesHere')}</span>
              </div>
            )}
            <InputGroupTextarea
              ref={inputRef}
              placeholder={messages.length > 0 ? t('aiHelper.placeholder.typeMessage') : t('aiHelper.placeholder.typeMessageOrSuggestion')}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className="min-h-[70px] group-hover:bg-card/60"
              disabled={!accountId}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && input.trim()) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              onPaste={attachments.handlePaste}
            />
            <InputGroupAddon align="block-end" className="group-hover:bg-card/60">
              <AttachFileButton
                accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.heic,.heif,.docx,.xlsx,.csv,.txt"
                disabled={!accountId}
                onFiles={attachments.addFiles}
                label={t('aiHelper.attachFile')}
              />
              <InputGroupButton
                variant="default"
                className="rounded-full ml-auto"
                size="icon-sm"
                disabled={!input.trim() || isStreaming || !accountId || attachments.uploading || !linkState}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSend();
                }}
              >
                <ArrowUp className="h-4 w-4" />
                <span className="sr-only">{t('common.send')}</span>
              </InputGroupButton>
            </InputGroupAddon>
          </InputGroup>
        </motion.div>
        <div className="px-4 pb-4 w-full flex items-center justify-center">
          <span className="text-muted-foreground text-xs">{t('aiHelper.disclosure')}</span>
        </div>
      </div>
    </div>
  );
}

function TriggerPill({ className }: { className?: string }) {
  const { t } = useTranslation();
  const { togglePanel } = useAIPanel();

  return (
    <motion.button
      type="button"
      onClick={togglePanel}
      className={cn('shadow-xs rounded-full cursor-pointer p-1 flex flex-row items-center justify-between bg-card border border-border-heavy', className)}
      whileHover={{ scale: 1.05 }}
      transition={{ duration: 0.2 }}
    >
      <span className="text-muted-foreground text-sm mx-4">{t('aiHelper.trigger.askAnything')}</span>
      <span className="size-8 rounded-full bg-primary flex items-center justify-center shrink-0">
        <ArrowUp className="size-4 text-primary-foreground" />
      </span>
    </motion.button>
  );
}

// Compact chat button for mobile, where the full pill crowds the page; the label stays as its
// accessible name.
function TriggerIcon({ className, style }: { className?: string; style?: CSSProperties }) {
  const { t } = useTranslation();
  const { togglePanel } = useAIPanel();

  return (
    <button
      type="button"
      onClick={togglePanel}
      aria-label={t('aiHelper.trigger.askAnything')}
      style={style}
      className={cn('size-11 rounded-full bg-primary text-primary-foreground shadow-md flex items-center justify-center cursor-pointer', className)}
    >
      <MessagesSquare className="size-5" />
    </button>
  );
}

// Desktop trigger, fixed to the viewport (shows when the panel is closed)
export function AITriggerButton() {
  const { isOpen } = useAIPanel();
  const isMobile = useMediaQuery(MOBILE_QUERY);

  if (isOpen || isMobile) {
    return null;
  }

  return <TriggerPill className="fixed z-[110] bottom-4 right-4" />;
}

// Height of the tallest bottom action bar (`data-bottom-bar`) currently rendered in `root`, kept
// current as bars mount, unmount and resize.
function useBottomBarHeight(rootRef: RefObject<HTMLElement | null>, active: boolean) {
  const [height, setHeight] = useState(0);

  useEffect(() => {
    const root = rootRef.current?.parentElement;
    if (!active || !root || typeof ResizeObserver === 'undefined') return;

    const bars = () => [...root.querySelectorAll<HTMLElement>('[data-bottom-bar]')];
    const measure = () => setHeight(Math.max(0, ...bars().map((bar) => bar.offsetHeight)));
    const resize = new ResizeObserver(measure);
    const track = () => {
      resize.disconnect();
      // Border box: a bar's home-indicator padding can change on its own (Safari's toolbar showing
      // or hiding) without changing its content box.
      for (const bar of bars()) resize.observe(bar, { box: 'border-box' });
      measure();
    };
    // Content updates (streamed chat replies, re-renders) mutate the page constantly; only a bar
    // arriving or leaving needs re-tracking, and the ResizeObserver covers height changes.
    const touchesBar = (node: Node) =>
      node instanceof Element && (node.matches('[data-bottom-bar]') || node.querySelector('[data-bottom-bar]') !== null);
    const mutations = new MutationObserver((records) => {
      if (records.some((record) => [...record.addedNodes, ...record.removedNodes].some(touchesBar))) track();
    });
    mutations.observe(root, { childList: true, subtree: true });
    track();
    return () => {
      mutations.disconnect();
      resize.disconnect();
    };
  }, [rootRef, active]);

  return height;
}

// Mobile trigger. A sticky anchor that adds no page height of its own, so it never lengthens the
// scroll or pushes a page's own sticky bar off the bottom; with a footer it reserves the icon's
// height so the icon rests above the footer links. The icon lifts above any bottom action bar
// marked `data-bottom-bar` and stays below the z-50 overlay layer (dialogs, drawers, cookie banner).
export function AIStickyTrigger({ reserveSpace }: { reserveSpace: boolean }) {
  const { isOpen } = useAIPanel();
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const ref = useRef<HTMLDivElement>(null);
  const active = isMobile && !isOpen;
  const barHeight = useBottomBarHeight(ref, active);

  if (!active) {
    return null;
  }

  return (
    <div ref={ref} className={cn('sticky bottom-0 z-40 pointer-events-none', reserveSpace ? 'h-15' : 'h-0')}>
      <TriggerIcon
        style={{ bottom: barHeight ? `calc(${barHeight}px + 1rem)` : 'max(1rem, env(safe-area-inset-bottom))' }}
        className="absolute right-4 pointer-events-auto"
      />
    </div>
  );
}

// Mobile full-screen overlay (replaces drawer to avoid keyboard/viewport conflicts)
export function AIMobileFullScreen({ context = 'dashboard' }: AIPanelContentProps) {
  const { isOpen } = useAIPanel();
  const isMobile = useMediaQuery(MOBILE_QUERY);

  if (!isMobile || !isOpen) {
    return null;
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="ai-mobile-fullscreen"
          initial={{ opacity: 0, y: '100%' }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: '100%' }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="fixed inset-0 z-[120] bg-background"
        >
          <AIPanelContent context={context} />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
